import React from 'react';
import { FilledButton, OutlinedButton, Text } from '@make-software/tg-mini-app-ui';

import {
  Section,
  Hint,
  FooterRow,
  OtpBox,
  OtpRow,
  OtpValue,
  OtpMetaRow,
  OtpPill,
  InlineLinkButton
} from './install-to-home-screen.styled';

export type OTPState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; code: string; expiresAtMs: number }
  | { status: 'error'; message: string };

interface OtpSectionProps {
  otpState: OTPState;
  disabled?: boolean;
  otpCopied: boolean;
  onRequestOtp: () => void;
  onCopyOtp: () => void;
}

export const OtpSection: React.FC<OtpSectionProps> = ({
  otpState,
  disabled,
  otpCopied,
  onRequestOtp,
  onCopyOtp
}) => {
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (otpState.status !== 'ready') return;

    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [otpState.status]);

  const formatTtl = (expiresAtMs: number) => {
    const diff = Math.max(0, expiresAtMs - nowMs);
    const totalSec = Math.ceil(diff / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    if (m >= 60) {
      const h = Math.floor(m / 60);
      const mm = m % 60;
      return `${h}h ${mm}m`;
    }
    return `${m}m ${String(s).padStart(2, '0')}s`;
  };

  const otpTtl = otpState.status === 'ready' ? formatTtl(otpState.expiresAtMs) : undefined;

  return (
    <Section>
      <Text weight={1} size="sm" color="white">
        Your verification code
      </Text>

      <Hint>
        You’ll enter this code <b>inside the Home Screen app</b> after opening TEA from the new
        icon.
      </Hint>

      <OtpBox>
        {otpState.status === 'idle' && (
          <>
            <Hint>Generate a one-time code to link the installed app to your account.</Hint>
            <FilledButton
              title="Generate code"
              onClick={onRequestOtp}
              disabled={disabled}
              borderRadius="0"
            />
          </>
        )}

        {otpState.status === 'loading' && (
          <>
            <Hint>Generating code…</Hint>
            <OtpRow>
              <OtpValue aria-label="OTP loading">••••••</OtpValue>
            </OtpRow>
          </>
        )}

        {otpState.status === 'error' && (
          <>
            <Hint>{otpState.message}</Hint>
            <FooterRow>
              <OutlinedButton title="Try again" onClick={onRequestOtp} height={28} width="96px" />
            </FooterRow>
          </>
        )}

        {otpState.status === 'ready' && (
          <>
            <OtpRow>
              <OtpValue aria-label="OTP code">{otpState.code}</OtpValue>
            </OtpRow>

            <OtpMetaRow>
              <OtpPill>Expires in {otpTtl}</OtpPill>
              <InlineLinkButton type="button" onClick={onCopyOtp}>
                {otpCopied ? 'Copied ✓' : 'Copy code'}
              </InlineLinkButton>
            </OtpMetaRow>

            <Hint>
              If it expires, generate a new one. Code is <b>one-time</b>.
            </Hint>
          </>
        )}
      </OtpBox>
    </Section>
  );
};
